import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

// ---------------------------------------------------------------------------
// Default template config — matches the original hardcoded appearance
// ---------------------------------------------------------------------------
export const DEFAULT_TEMPLATE_CONFIG = {
  logo_key: null,
  logo_url: null,
  company_name_override: null,
  company_address: '',
  company_phone: '',
  company_email: '',
  company_website: '',
  company_tax_id: '',
  colors: {
    primary: '#18181b',
    accent: '#f4f4f5',
    text: '#27272a',
    background: '#ffffff',
  },
  font_family: 'Helvetica',
  header_layout: 'standard',
  show_company_details: true,
  show_notes_section: true,
  show_footer_section: true,
};

// ---------------------------------------------------------------------------
// Font mapping — each family maps to normal + bold variants
// ---------------------------------------------------------------------------
const FONT_MAP = {
  Helvetica: { normal: 'Helvetica', bold: 'Helvetica-Bold' },
  'Times-Roman': { normal: 'Times-Roman', bold: 'Times-Bold' },
  Courier: { normal: 'Courier', bold: 'Courier-Bold' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (d) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const TERMS_MAP = {
  due_on_receipt: 'Due on receipt',
  net_10: 'Net 10',
  net_15: 'Net 15',
  net_30: 'Net 30',
  net_45: 'Net 45',
  net_60: 'Net 60',
  net_90: 'Net 90',
};

const DEFAULT_COLUMNS = [
  { key: 'description', label: 'Description', type: 'text', required: true },
  { key: 'quantity', label: 'Quantity', type: 'number', default: 1 },
  { key: 'unit_price', label: 'Unit Price', type: 'currency', default: 0 },
  { key: 'amount', label: 'Amount', type: 'currency', formula: 'quantity * unit_price' },
];

function normalizeColumns(columns) {
  if (!columns) return DEFAULT_COLUMNS;
  return columns.map(col => col.type === 'computed' ? { ...col, type: 'currency' } : col);
}

function colFlex(col) {
  if (col.key === 'description') return 3;
  if (col.type === 'text') return 1.2;
  if (col.type === 'boolean') return 0.5;
  if (col.type === 'date') return 1;
  return 0.9;
}

function fmtCell(col, value) {
  if (col.type === 'currency') return fmt(value);
  if (col.type === 'percent') return value != null ? `${value}%` : '—';
  if (col.type === 'boolean') return value ? 'Yes' : 'No';
  if (col.type === 'date') return value ? fmtDate(value) : '—';
  if (col.type === 'number') return value != null ? String(value) : '—';
  return value ?? '—';
}

// ---------------------------------------------------------------------------
// Dynamic styles — generated per-render from template config
// ---------------------------------------------------------------------------
function getStyles(c) {
  const fonts = FONT_MAP[c.font_family] || FONT_MAP.Helvetica;
  // Derive a muted version of the text color for labels
  const mutedColor = '#a1a1aa';
  const subtextColor = '#71717a';
  const bodyColor = '#3f3f46';
  const dividerColor = '#e4e4e7';
  const altRowBg = c.colors.accent;

  return StyleSheet.create({
    page: {
      paddingTop: 40,
      paddingBottom: 60,
      paddingHorizontal: 40,
      fontFamily: fonts.normal,
      fontSize: 10,
      color: c.colors.text,
      backgroundColor: c.colors.background,
    },
    header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
    companyName: { fontSize: 18, fontFamily: fonts.bold, color: c.colors.primary },
    companyDetail: { fontSize: 8, color: bodyColor, lineHeight: 1.5 },
    invoiceTitle: { fontSize: 24, fontFamily: fonts.bold, color: c.colors.primary, textAlign: 'right' },
    invoiceNumber: { fontSize: 10, color: subtextColor, textAlign: 'right', marginTop: 2 },

    divider: { borderBottomWidth: 1, borderBottomColor: dividerColor, marginBottom: 20 },

    infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 24 },
    sectionLabel: { fontSize: 8, fontFamily: fonts.bold, color: mutedColor, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 },
    billToText: { fontSize: 10, color: bodyColor, lineHeight: 1.5 },
    billToName: { fontSize: 10, fontFamily: fonts.bold, color: c.colors.primary, lineHeight: 1.5 },
    metaRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 3 },
    metaLabel: { fontSize: 9, color: mutedColor, width: 80, textAlign: 'right' },
    metaValue: { fontSize: 9, color: bodyColor, width: 100, textAlign: 'right', fontFamily: fonts.bold },

    table: { marginBottom: 20 },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: altRowBg,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      paddingVertical: 6,
      columnGap: 8,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: dividerColor,
    },
    tableHeaderText: { fontSize: 8, fontFamily: fonts.bold, color: subtextColor, textTransform: 'uppercase', letterSpacing: 0.5 },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 7,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: altRowBg,
      columnGap: 8,
    },
    tableRowAlt: {
      flexDirection: 'row',
      paddingVertical: 7,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: altRowBg,
      columnGap: 8,
      backgroundColor: '#fafafa',
    },

    totalsWrap: { alignItems: 'flex-end', marginBottom: 20 },
    totalsBox: { width: 220 },
    totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 },
    totalLabel: { fontSize: 10, color: subtextColor },
    totalValue: { fontSize: 10, color: bodyColor },
    totalLabelBold: { fontSize: 10, fontFamily: fonts.bold, color: c.colors.primary },
    totalValueBold: { fontSize: 10, fontFamily: fonts.bold, color: c.colors.primary },
    totalDivider: { borderBottomWidth: 1, borderBottomColor: dividerColor, marginVertical: 2 },
    paidValue: { fontSize: 10, color: '#059669' },
    paidLabel: { fontSize: 10, color: '#059669' },

    notesSection: { paddingTop: 16, borderTopWidth: 1, borderTopColor: altRowBg },
    notesText: { fontSize: 9, color: '#52525b', lineHeight: 1.5 },
    footerSection: { paddingTop: 10 },

    watermark: {
      position: 'absolute',
      top: '40%',
      left: '15%',
      fontSize: 72,
      fontFamily: fonts.bold,
      color: '#ef444450',
      transform: 'rotate(-30deg)',
      letterSpacing: 8,
    },
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const InvoicePDF = ({ invoice, companyName, templateConfig }) => {
  const c = { ...DEFAULT_TEMPLATE_CONFIG, ...templateConfig, colors: { ...DEFAULT_TEMPLATE_CONFIG.colors, ...templateConfig?.colors } };
  const s = getStyles(c);

  const displayName = c.company_name_override || companyName;
  const cust = invoice.customers || {};
  const termsLabel = TERMS_MAP[invoice.terms] || invoice.terms || '—';
  const columns = normalizeColumns(invoice.column_config);

  const colCount = columns.length;
  const useLandscape = colCount > 6;
  const scale = colCount <= 5 ? 1 : colCount <= 8 ? 0.85 : 0.7;
  const sz = (base) => Math.round(base * scale * 10) / 10;

  const hasCompanyDetails = c.show_company_details && (c.company_address || c.company_phone || c.company_email || c.company_website || c.company_tax_id);

  return (
    <Document>
      <Page size="A4" orientation={useLandscape ? 'landscape' : 'portrait'} style={[s.page, { fontSize: sz(10) }]}>
        {invoice.voided_at && <Text style={s.watermark}>VOIDED</Text>}

        {/* Header */}
        <View style={s.header}>
          <View>
            <Text style={[s.companyName, { fontSize: sz(18) }]}>{displayName}</Text>
            {hasCompanyDetails && (
              <View style={{ marginTop: 4 }}>
                {c.company_address && <Text style={s.companyDetail}>{c.company_address}</Text>}
                {c.company_phone && <Text style={s.companyDetail}>{c.company_phone}</Text>}
                {c.company_email && <Text style={s.companyDetail}>{c.company_email}</Text>}
                {c.company_website && <Text style={s.companyDetail}>{c.company_website}</Text>}
                {c.company_tax_id && <Text style={s.companyDetail}>Tax ID: {c.company_tax_id}</Text>}
              </View>
            )}
          </View>
          <View>
            <Text style={[s.invoiceTitle, { fontSize: sz(24) }]}>INVOICE</Text>
            <Text style={[s.invoiceNumber, { fontSize: sz(10) }]}>{invoice.invoice_number}</Text>
          </View>
        </View>

        <View style={s.divider} />

        {/* Bill To + Invoice Meta */}
        <View style={s.infoRow}>
          <View style={{ maxWidth: useLandscape ? 340 : 260 }}>
            <Text style={s.sectionLabel}>Bill To</Text>
            <Text style={[s.billToName, { fontSize: sz(10) }]}>{cust.display_name}</Text>
            {cust.company_name && cust.company_name !== cust.display_name && (
              <Text style={[s.billToText, { fontSize: sz(10) }]}>{cust.company_name}</Text>
            )}
            {cust.address_line1 && <Text style={[s.billToText, { fontSize: sz(10) }]}>{cust.address_line1}</Text>}
            {cust.address_line2 && <Text style={[s.billToText, { fontSize: sz(10) }]}>{cust.address_line2}</Text>}
            {(cust.city || cust.state) && (
              <Text style={[s.billToText, { fontSize: sz(10) }]}>
                {[cust.city, cust.state].filter(Boolean).join(', ')} {cust.zip}
              </Text>
            )}
            {cust.email && <Text style={[s.billToText, { fontSize: sz(10) }]}>{cust.email}</Text>}
            {cust.phone && <Text style={[s.billToText, { fontSize: sz(10) }]}>{cust.phone}</Text>}
          </View>
          <View>
            <View style={s.metaRow}>
              <Text style={[s.metaLabel, { fontSize: sz(9) }]}>Invoice Date</Text>
              <Text style={[s.metaValue, { fontSize: sz(9) }]}>{fmtDate(invoice.invoice_date)}</Text>
            </View>
            <View style={s.metaRow}>
              <Text style={[s.metaLabel, { fontSize: sz(9) }]}>Due Date</Text>
              <Text style={[s.metaValue, { fontSize: sz(9) }]}>{fmtDate(invoice.due_date)}</Text>
            </View>
            <View style={s.metaRow}>
              <Text style={[s.metaLabel, { fontSize: sz(9) }]}>Terms</Text>
              <Text style={[s.metaValue, { fontSize: sz(9) }]}>{termsLabel}</Text>
            </View>
          </View>
        </View>

        {/* Line Items Table */}
        <View style={s.table}>
          <View style={s.tableHeader}>
            {columns.map((col) => (
              <Text
                key={col.key}
                style={[s.tableHeaderText, { flex: colFlex(col), textAlign: col.type === 'text' ? 'left' : 'right', fontSize: sz(8) }]}
              >
                {col.label}
              </Text>
            ))}
          </View>
          {(invoice.line_items || []).map((li, i) => (
            <View key={li.id || i} style={i % 2 === 1 ? s.tableRowAlt : s.tableRow}>
              {columns.map((col) => (
                <Text
                  key={col.key}
                  style={{ flex: colFlex(col), textAlign: col.type === 'text' ? 'left' : 'right', fontSize: sz(10) }}
                >
                  {fmtCell(col, li[col.key])}
                </Text>
              ))}
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={s.totalsWrap}>
          <View style={s.totalsBox}>
            <View style={s.totalRow}>
              <Text style={[s.totalLabel, { fontSize: sz(10) }]}>Subtotal</Text>
              <Text style={[s.totalValue, { fontSize: sz(10) }]}>{fmt(invoice.subtotal)}</Text>
            </View>
            {Number(invoice.tax_amount) > 0 && (
              <View style={s.totalRow}>
                <Text style={[s.totalLabel, { fontSize: sz(10) }]}>Tax ({(Number(invoice.tax_rate) * 100).toFixed(1)}%)</Text>
                <Text style={[s.totalValue, { fontSize: sz(10) }]}>{fmt(invoice.tax_amount)}</Text>
              </View>
            )}
            <View style={s.totalDivider} />
            <View style={s.totalRow}>
              <Text style={[s.totalLabelBold, { fontSize: sz(10) }]}>Total</Text>
              <Text style={[s.totalValueBold, { fontSize: sz(10) }]}>{fmt(invoice.total_amount)}</Text>
            </View>
            {invoice.amount_paid > 0 && (
              <View style={s.totalRow}>
                <Text style={[s.paidLabel, { fontSize: sz(10) }]}>Amount Paid</Text>
                <Text style={[s.paidValue, { fontSize: sz(10) }]}>-{fmt(invoice.amount_paid)}</Text>
              </View>
            )}
            {invoice.balance_due > 0 && (
              <>
                <View style={s.totalDivider} />
                <View style={s.totalRow}>
                  <Text style={[s.totalLabelBold, { fontSize: sz(10) }]}>Balance Due</Text>
                  <Text style={[s.totalValueBold, { fontSize: sz(10) }]}>{fmt(invoice.balance_due)}</Text>
                </View>
              </>
            )}
          </View>
        </View>

        {/* Notes */}
        {c.show_notes_section && invoice.notes && (
          <View style={s.notesSection}>
            <Text style={s.sectionLabel}>Notes</Text>
            <Text style={s.notesText}>{invoice.notes}</Text>
          </View>
        )}

        {/* Footer / Terms */}
        {c.show_footer_section && invoice.footer_note && (
          <View style={s.footerSection}>
            <Text style={s.sectionLabel}>Terms</Text>
            <Text style={s.notesText}>{invoice.footer_note}</Text>
          </View>
        )}
      </Page>
    </Document>
  );
};

export default InvoicePDF;
